import React, { useCallback, useEffect, useRef, useState } from "react";
import IconButton from "@material-ui/core/IconButton";
import { Close as CloseIcon } from "@material-ui/icons";
import Modal from "./modal";
import DocumentPreview from "./DocumentPreview";
import { DOCUMENT_ACTION } from "../redux/action";

function inferMimeType(ext) {
  const e = (ext || "").toLowerCase();
  if (["jpg", "jpeg", "png", "gif", "webp", "svg", "bmp", "avif"].includes(e)) {
    return `image/${e === "svg" ? "svg+xml" : e === "jpg" ? "jpeg" : e}`;
  }
  if (e === "pdf") return "application/pdf";
  if (e === "docx") {
    return "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
  }
  if (e === "doc") return "application/msword";
  if (e === "xlsx") {
    return "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
  }
  if (e === "xls") return "application/vnd.ms-excel";
  return "";
}

function deriveFileMeta(url, fallbackName = "") {
  let pathname = "";
  try {
    pathname = new URL(url).pathname;
  } catch (e) {
    pathname = url;
  }
  let key = pathname.replace(/^\/+/, "");
  try {
    key = decodeURIComponent(key);
  } catch (e) {
    /* keep encoded if decode fails */
  }
  const nameFromUrl = key.split("/").pop() || "";
  const name = String(fallbackName || nameFromUrl || "File").trim() || "File";
  const ext = (name.includes(".") ? name.split(".").pop() : "").toLowerCase();
  return { fileURL: key, document_name: name, ext };
}

export default function EfilingDocumentPreviewModal({
  open,
  sourceUrl,
  documentName: documentNameProp = "",
  clientDocumentId,
  onClose,
  title = "Preview",
}) {
  const [blobUrl, setBlobUrl] = useState("");
  const [docType, setDocType] = useState("");
  const [documentName, setDocumentName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const blobUrlRef = useRef("");

  useEffect(() => {
    blobUrlRef.current = blobUrl;
  }, [blobUrl]);

  const revokeBlob = useCallback(() => {
    if (blobUrlRef.current) {
      try {
        URL.revokeObjectURL(blobUrlRef.current);
      } catch (e) {
        /* ignore */
      }
      blobUrlRef.current = "";
    }
  }, []);

  const resetPreview = useCallback(() => {
    setBlobUrl("");
    setDocType("");
    setDocumentName("");
    setError("");
    setLoading(false);
    revokeBlob();
  }, [revokeBlob]);

  const handleClose = useCallback(() => {
    resetPreview();
    if (typeof onClose === "function") onClose();
  }, [onClose, resetPreview]);

  useEffect(() => {
    return () => {
      revokeBlob();
    };
  }, [revokeBlob]);

  useEffect(() => {
    if (!open || !sourceUrl) return undefined;

    let cancelled = false;

    const loadPreview = async () => {
      revokeBlob();
      const meta = deriveFileMeta(sourceUrl, documentNameProp);
      const document_type = inferMimeType(meta.ext);
      setDocType(document_type);
      setDocumentName(meta.document_name);
      setLoading(true);
      setError("");
      setBlobUrl("");
      try {
        const response = await DOCUMENT_ACTION.downloadViewFile({
          fileURL: meta.fileURL,
          document_name: meta.document_name,
          document_type,
          client_document_id: clientDocumentId || undefined,
          type: "Preview",
        });
        const raw = response && response.raw;
        if (!raw) throw new Error("No preview returned");
        if (cancelled) {
          try {
            URL.revokeObjectURL(raw);
          } catch (e) {
            /* ignore */
          }
          return;
        }
        setBlobUrl(raw);
        blobUrlRef.current = raw;
      } catch (e) {
        if (!cancelled) setError("Could not load preview for this file.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadPreview();

    return () => {
      cancelled = true;
    };
  }, [open, sourceUrl, documentNameProp, clientDocumentId, revokeBlob]);

  return (
    <Modal
      title={null}
      open={Boolean(open)}
      handleClose={handleClose}
      maxWidth
      className="efiling-create-modal efiling-preview-modal"
    >
      <div className="efiling-modal-shell">
        <div className="efiling-modal-header-row">
          <h3 className="efiling-modal-title">{title}</h3>
          <IconButton
            aria-label="close"
            className="efiling-modal-close-btn"
            onClick={handleClose}
          >
            <CloseIcon />
          </IconButton>
        </div>

        <div className="efiling-preview-body">
          {loading ? (
            <div className="efiling-preview-loading">Loading preview…</div>
          ) : error ? (
            <div className="efiling-preview-fallback">
              <p>{error}</p>
            </div>
          ) : blobUrl ? (
            <DocumentPreview
              blobUrl={blobUrl}
              documentType={docType}
              documentName={documentName}
              className="efiling-preview-frame"
            />
          ) : null}
        </div>
      </div>
    </Modal>
  );
}
